import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShoppingBasket, User, LogOut, Heart, Bell, LayoutDashboard, ShieldCheck, Trash2, Crown } from "lucide-react";

export const Header = () => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [delOpen, setDelOpen] = useState(false);

  const deleteAccount = async () => {
    try {
      await api.delete("/account");
      toast.success("Compte et données supprimés");
      localStorage.removeItem("pmc_token");
      localStorage.removeItem("pmc_basket");
      window.location.href = "/";
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      data-testid={`nav-${to.replace(/\//g, "") || "home"}`}
      className={`text-sm font-medium transition-colors hover:text-primary ${
        loc.pathname === to ? "text-primary" : "text-muted-foreground"
      }`}
    >
      {children}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="brand-logo" className="group flex items-center gap-2">
          <img src="/logo192.png" alt="PanierMoinsCher" className="h-9 w-9 rounded-xl shadow-sm transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-rotate-6" />
          <span className="font-display text-lg font-800 tracking-tight text-secondary">
            Panier<span className="text-primary">MoinsCher</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <NavLink to="/comparateur">Comparateur</NavLink>
          {user && <NavLink to="/paniers">Mes paniers</NavLink>}
          {user && <NavLink to="/alertes">Alertes</NavLink>}
          <NavLink to="/premium">Premium</NavLink>
          {user?.role === "admin" && <NavLink to="/admin">Back-office</NavLink>}
        </nav>

        <div className="flex items-center gap-3">
          {!user ? (
            <>
              <Button variant="ghost" size="sm" data-testid="header-login-btn" onClick={() => nav("/connexion")}>
                Connexion
              </Button>
              <Button size="sm" className="rounded-full" data-testid="header-register-btn" onClick={() => nav("/inscription")}>
                Créer un compte
              </Button>
            </>
          ) : (
            <>
            {user.premium ? (
              <span className="hidden items-center gap-1 rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent sm:flex" data-testid="premium-indicator"><Crown className="h-3 w-3" /> Premium</span>
            ) : (
              <Button variant="ghost" size="sm" className="hidden rounded-full text-accent sm:flex" onClick={() => nav("/premium")} data-testid="upgrade-btn"><Crown className="mr-1 h-4 w-4" /> Premium</Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full gap-2" data-testid="user-menu-btn">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">{user.name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => nav("/comparateur")} data-testid="menu-comparateur">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Comparateur
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/paniers")} data-testid="menu-baskets">
                  <ShoppingBasket className="mr-2 h-4 w-4" /> Mes paniers
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/alertes")} data-testid="menu-alerts">
                  <Bell className="mr-2 h-4 w-4" /> Alertes prix
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/abonnement")} data-testid="menu-subscription">
                  <Crown className="mr-2 h-4 w-4" /> Mon abonnement
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Back-office
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Déconnexion
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDelOpen(true)} data-testid="menu-delete-account" className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" /> Supprimer mon compte
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          )}
        </div>
      </div>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer votre compte ?</AlertDialogTitle>
            <AlertDialogDescription>
              Conformément au RGPD, votre compte et toutes vos données (paniers sauvegardés,
              favoris, alertes, historique) seront définitivement supprimés. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-account">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-account">
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
};
